<template>
    <div class="ds-dossier-body">
        <div class="ds-dossier-subhead">
            <button type="button" class="ds-dossier-back" :disabled="isSaving" @click="emit('back')">
                <svg width="7" height="11" viewBox="0 0 9 13" fill="currentColor" aria-hidden="true"><path d="M3.37716 6.5002L8.12091 11.244L6.76583 12.599L0.666992 6.5002L6.76583 0.401367L8.12091 1.75645L3.37716 6.5002Z" /></svg>
                {{ backLabel }}
            </button>
        </div>
        <span class="ds-dossier-subtitle">Доступ и пароль</span>

        <div class="ds-dossier-pairs">
            <span>Логин</span><span class="is-strong">{{ person.phone || DASH }}</span>
        </div>

        <div class="ds-dossier-field">
            <span class="ds-dossier-field-label">Новый пароль</span>
            <div class="ds-dossier-password">
                <span class="ds-dossier-password-field">
                    <input
                        v-model="password"
                        :type="isVisible ? 'text' : 'password'"
                        maxlength="64"
                        autocomplete="new-password"
                        aria-label="Новый пароль"
                        :disabled="isSaving"
                        @input="isCopied = false"
                    >
                    <button type="button" class="ds-dossier-icon-button" style="width: 20px; height: 20px" :aria-label="isVisible ? 'Скрыть пароль' : 'Показать пароль'" @click="isVisible = !isVisible">
                        <svg v-if="isVisible" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12z" /><circle cx="12" cy="12" r="3" /></svg>
                        <icon v-else width="16" height="16" vb-size="24"><IconEyeX /></icon>
                    </button>
                    <button type="button" class="ds-dossier-icon-button" style="width: 20px; height: 20px" :aria-label="isCopied ? 'Пароль скопирован' : 'Скопировать'" :title="isCopied ? 'Пароль скопирован' : 'Скопировать'" :disabled="!password" @click="copyPassword">
                        <icon width="16" height="16" vb-size="24"><IconCopy /></icon>
                    </button>
                </span>
                <button type="button" class="ds-dossier-password-generate" :disabled="isSaving" @click="generatePassword">
                    <img :src="refreshIcon" width="14" height="14" alt="">Сгенерировать
                </button>
            </div>
            <span class="ds-dossier-tile-note">{{ isCopied ? 'Пароль скопирован в буфер обмена.' : 'Минимум 8 символов. Сгенерированный пароль уже подставлен — его можно изменить.' }}</span>
            <span v-if="password && password.length < MIN_LENGTH" class="ds-dossier-field-error">Минимум {{ MIN_LENGTH }} символов</span>
        </div>

        <checkbox v-model="sendToEmail" class="ds-dossier-checkbox" size="sm" layout="inline" :disabled="isSaving || !canSendEmail">Отправить пароль на e-mail</checkbox>

        <div v-if="sendToEmail" class="ds-dossier-grid">
            <div class="ds-dossier-field">
                <span class="ds-dossier-field-label">E-mail</span>
                <!-- E-mail из 1С (User.public_api_fields): только существующий адрес, изменить здесь нельзя. -->
                <span v-if="emailLocked" class="ds-dossier-locked" :title="emailLockTitle"><span>{{ email || DASH }}</span><LockIcon /></span>
                <base-input v-else v-model="email" :label="false" max-length="50" :disabled="isSaving" />
                <span v-if="email && !isEmailValid" class="ds-dossier-field-error">Некорректный e-mail</span>
            </div>
        </div>

        <div v-if="emailLocked" class="ds-dossier-note">
            {{ canSendEmail ? 'E-mail заполняется из 1С — отправить можно только на указанный адрес.' : 'E-mail заполняется из 1С и не указан — отправка недоступна, пароль можно сохранить без неё.' }}
            Без отправки — передайте пароль сотруднику сами (кнопка «копировать»).
        </div>
        <div v-else class="ds-dossier-note">Если e-mail отличается от указанного в карточке, он обновится. Без отправки — передайте пароль сотруднику сами (кнопка «копировать»).</div>
        <div class="ds-dossier-info is-warning">Старый пароль перестанет работать сразу после сохранения.</div>
    </div>

    <div class="ds-dossier-footer">
        <span v-if="error" class="ds-dossier-footer-message ds-dossier-field-error" role="alert">{{ error }}</span>
        <button type="button" class="ds-glass-btn ds-glass-btn-secondary" :disabled="isSaving" @click="emit('back')">Отмена</button>
        <button type="button" class="ds-glass-btn ds-glass-btn-primary" :disabled="!canSave" @click="save">{{ isSaving ? 'Сохранение…' : 'Сохранить пароль' }}</button>
    </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { useToast } from 'vue-toast-notification'
import { resetUserPassword } from '@/api/users'
import refreshIcon from '@/assets/icons/IconRefreshCcw.svg'
import IconCopy from '@/ui/icons/IconCopy.vue'
import IconEyeX from '@/ui/icons/IconEyeX.vue'
import { type DossierPerson, DASH, EMAIL_PATTERN, LOCKS_UNKNOWN_TITLE, ONE_C_LOCK_TITLE, describeWriteError, isApiLocked } from './dossierModel'
import { LockIcon } from './dossierIcons'

// POST /api/user/{User UUID}/reset-password/ {password, send_to_email, email?}.
// Пароль живёт только в состоянии подэкрана: не сохраняется в store/storage и не журналируется.
const props = defineProps<{
    person: DossierPerson
    backLabel: string
}>()
const emit = defineEmits<{ back: [], saved: [] }>()

const toast = useToast({ position: 'bottom-right' })
const MIN_LENGTH = 8

const password = ref('')
const isVisible = ref(true)
const isCopied = ref(false)
const sendToEmail = ref(false)
const email = ref(props.person.email ?? '')
const isSaving = ref(false)
const error = ref('')
let copyTimeout: ReturnType<typeof setTimeout> | undefined

// Закреплённый (или непроверенный) email: адрес не меняется, отправка — только на существующий корректный.
const emailLocked = computed(() => isApiLocked(props.person.publicApiFields, 'email'))
const emailLockTitle = computed(() => (props.person.publicApiFields === null ? LOCKS_UNKNOWN_TITLE : ONE_C_LOCK_TITLE))
const canSendEmail = computed(() => !emailLocked.value || EMAIL_PATTERN.test(props.person.email ?? ''))
watch(emailLocked, (value) => {
    if (value) email.value = props.person.email ?? ''
    if (!canSendEmail.value) sendToEmail.value = false
}, { immediate: true })
const isEmailValid = computed(() => EMAIL_PATTERN.test(email.value))
const canSave = computed(() => !isSaving.value && password.value.length >= MIN_LENGTH && (!sendToEmail.value || isEmailValid.value))

// Прежняя генерация EmployeeSettings: crypto.getRandomValues, строчная/заглавная/цифра, 12 символов.
function generatePassword() {
    isCopied.value = false
    const lowerCase = 'abcdefghijklmnopqrstuvwxyz'
    const upperCase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
    const digits = '0123456789'
    const allCharacters = lowerCase + upperCase + digits
    const randomIndex = (length: number) => crypto.getRandomValues(new Uint32Array(1))[0] % length
    const characters = [
        lowerCase[randomIndex(lowerCase.length)],
        upperCase[randomIndex(upperCase.length)],
        digits[randomIndex(digits.length)],
    ]
    while (characters.length < 12) characters.push(allCharacters[randomIndex(allCharacters.length)])
    password.value = characters.sort(() => crypto.getRandomValues(new Uint32Array(1))[0] - 0x80000000).join('')
}

async function copyPassword() {
    if (!password.value || !navigator.clipboard) return
    try {
        await navigator.clipboard.writeText(password.value)
        isCopied.value = true
        if (copyTimeout) clearTimeout(copyTimeout)
        copyTimeout = setTimeout(() => { isCopied.value = false }, 3000)
    } catch {
        toast.error('Не удалось скопировать пароль')
    }
}

async function save() {
    if (!canSave.value) return
    isSaving.value = true
    error.value = ''
    // send_to_email=false не передаёт email.
    const data = {
        password: password.value,
        send_to_email: sendToEmail.value,
        ...(sendToEmail.value ? { email: email.value } : {}),
    }
    try {
        const response = await resetUserPassword(props.person.id, data)
        if (response.data.status === 'error') {
            error.value = response.data.msg || 'Не удалось сменить пароль'
            return
        }
        // Успех сброса не подтверждает доставку письма.
        toast.success(sendToEmail.value ? 'Пароль изменён, письмо отправлено на обработку' : 'Пароль изменён')
        password.value = ''
        emit('saved')
    } catch (caught: unknown) {
        // 400 (в т. ч. отказ закреплённого email) — пароль тоже не изменён.
        error.value = describeWriteError(caught, 'Пароль не изменён')
    } finally {
        isSaving.value = false
    }
}

generatePassword()

onBeforeUnmount(() => {
    if (copyTimeout) clearTimeout(copyTimeout)
    password.value = ''
})
</script>
